import { Dispatch, SetStateAction } from "react";
import { cn } from "@/lib/utils";
import { ScaleRatings } from "@/components/doctor-training/scale-ratings";
import { BinaryRatings } from "@/components/doctor-training/binary-ratings";
import { ReportManageActions } from "@/components/doctor-training/report-manage-actions/report-manage-actions";
import { createPath } from "@/service/routing/routing";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { StarIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export const FullReport = ({
  isOpenModal,
  setIsOpenModal,
  report: { details, anonTitle, id, rating, createdAt, comments, canManage },
  facilitySlug,
  unitSlug,
}: {
  isOpenModal: boolean;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  report: MedicalFacilityUnitReport;
  facilitySlug: string;
  unitSlug: string;
}) => {
  const { scaleRatings, binaryRatings, textAnswers } = details;

  const fullStars = Math.round(rating ?? 0);
  const emptyStars = 5 - fullStars;
  return (
    <Dialog open={isOpenModal} onOpenChange={setIsOpenModal}>
      <DialogContent
        className={"w-[calc(100vw-32px)] max-w-[1000px]"}
        withScrollbar={true}
      >
        <div className={cn("px-4 py-6", "sm:px-8", "md:px-16 md:py-12")}>
          <div
            className={
              "mb-3 flex flex-col items-center justify-between md:flex-row"
            }
          >
            <div className={"flex items-center justify-start gap-3"}>
              <div className={"text-2xl font-bold text-zinc-950"}>
                {rating.toFixed(1)}
              </div>
              <div className={"flex items-center justify-start gap-1"}>
                {Array.from({ length: fullStars }).map((_, i) => (
                  <StarIcon
                    fill={"#FE9C08"}
                    stroke={"#FE9C08"}
                    size={"18px"}
                    key={i}
                  />
                ))}
                {Array.from({ length: emptyStars }).map((_, i) => (
                  <StarIcon stroke={"#FE9C08"} size={"18px"} key={i} />
                ))}
              </div>
            </div>
            <div
              className={
                "inline-flex flex-col items-center gap-2 text-sm text-zinc-500 sm:flex-row"
              }
            >
              <span className={"font-semibold"}>{anonTitle}</span>
              <span className={"hidden sm:inline"}>·</span>
              <span>{createdAt}</span>
            </div>
          </div>

          {canManage && (
            <div
              className={"mb-6 flex items-center gap-8 text-sm text-zinc-500"}
            >
              <ReportManageActions
                editHref={createPath(
                  "residency_map.facility.unit.report.edit",
                  {
                    facilitySlug,
                    unitSlug,
                    reportId: id,
                  },
                )}
                deleteEndpoint={"residency_report.delete"}
                reportId={id}
              />
            </div>
          )}

          <div className={"@container mb-6"}>
            <div
              className={cn("@448:grid-cols-2 grid w-full grid-cols-1 gap-6")}
            >
              <ScaleRatings scaleRatings={scaleRatings} />
              <BinaryRatings single={true} binaryRatings={binaryRatings} />
            </div>
          </div>

          <Card>
            <CardContent className={"flex flex-col gap-4"}>
              <div className={"flex w-full flex-col gap-4"}>
                <div>
                  <div className={"text-sm font-semibold text-zinc-950"}>
                    {details.specialization.label}
                  </div>
                  <div className={"text-sm font-normal text-zinc-950"}>
                    {details.specialization.title}
                  </div>
                </div>
                {details.textAnswers.map(({ title, content }) => (
                  <div key={title}>
                    <div className={"text-sm font-semibold text-zinc-950"}>
                      {title}
                    </div>
                    <div className={"text-sm font-normal text-zinc-950"}>
                      {content}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        <DialogClose />
      </DialogContent>
    </Dialog>
  );
};
